import React, { useState, useEffect } from 'react';
import './ImageGallery.css';
import ImageModal from './ImageModal';
import useHumlanImages from '../../hooks/useHumlanImages';

// Helper functions
const formatTimestamp = (timestamp) => {
  if (!timestamp) return 'Okänd tid';
  // Format: YYYY-MM-DD_HH-MM-SS → DD/MM/YYYY HH:MM
  const parts = timestamp.split('_');
  if (parts.length !== 2) return timestamp;

  const [date, time] = parts;
  const [year, month, day] = date.split('-');
  const [hour, minute] = time.split('-');

  return `${day}/${month}/${year} ${hour}:${minute}`;
};

const formatBytes = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

// Thumbnail component with lazy loading
const ImageThumbnail = ({ image, sessionId, getImageBlobUrl }) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadImage = async () => {
      try {
        setLoading(true);
        const blobUrl = await getImageBlobUrl(image.filename);
        if (mounted) {
          setSrc(blobUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error('Failed to load thumbnail:', err);
        if (mounted) {
          setError(true);
          setLoading(false);
        }
      }
    };

    loadImage();

    return () => {
      mounted = false;
    };
  }, [image.filename, getImageBlobUrl]);

  if (loading) {
    return (
      <div className="image-item-thumbnail loading">
        <div className="spinner-border spinner-border-sm" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="image-item-thumbnail error">
        <span>⚠️</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={image.filename}
      className="image-item-thumbnail"
      loading="lazy"
    />
  );
};

const ImageGallery = ({ isOpen, onClose, sessionId, autoRefresh = false }) => {
  const [isClosing, setIsClosing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageBlobUrls, setImageBlobUrls] = useState({});

  // Use Humla images hook
  const {
    images,
    loading,
    error,
    refresh,
    getImageBlobUrl,
    hasImages,
    imageCount
  } = useHumlanImages(sessionId, {
    autoRefresh,
    refreshInterval: 10000, // 10 seconds
    fetchOnMount: false // Don't fetch on mount, fetch when opened
  });

  // Fetch images when gallery opens
  useEffect(() => {
    if (isOpen && sessionId) {
      console.log('[ImageGallery] Opening gallery for session:', sessionId);
      refresh().then(result => {
        console.log('[ImageGallery] Refresh result:', result);
      }).catch(err => {
        console.error('[ImageGallery] Refresh error:', err);
      });
    }
  }, [isOpen, sessionId, refresh]);

  // Reset closing state when opening
  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300); // Match animation duration
  };

  const handleImageClick = async (image) => {
    // Fetch blob URL if not already fetched
    if (!imageBlobUrls[image.filename]) {
      try {
        const blobUrl = await getImageBlobUrl(image.filename);
        setImageBlobUrls(prev => ({ ...prev, [image.filename]: blobUrl }));
        setSelectedImage({ ...image, url: blobUrl });
      } catch (err) {
        console.error('Failed to load image:', err);
      }
    } else {
      setSelectedImage({ ...image, url: imageBlobUrls[image.filename] });
    }
  };

  const handleCloseModal = () => {
    setSelectedImage(null);
  };

  const handleOverlayClick = (e) => {
    // Close if clicking directly on overlay (not on sidebar)
    if (e.target.classList.contains('image-gallery-overlay')) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`image-gallery-overlay ${isClosing ? 'closing' : ''}`}
        onClick={handleOverlayClick}
      >
        <div className={`image-gallery-sidebar ${isClosing ? 'closing' : ''}`}>
          {/* Header */}
          <div className="image-gallery-header">
            <h2>Bilder</h2>
            <button
              className="image-gallery-close"
              onClick={handleClose}
              aria-label="Stäng"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="image-gallery-content">
            {loading ? (
              <div className="image-gallery-loading">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Laddar bilder...</span>
                </div>
                <p>Laddar bilder...</p>
              </div>
            ) : error ? (
              <div className="image-gallery-error">
                <div className="image-gallery-error-icon">⚠️</div>
                <p>{error}</p>
                <button className="btn btn-sm btn-primary" onClick={refresh}>
                  Försök igen
                </button>
              </div>
            ) : imageCount === 0 ? (
              <div className="image-gallery-empty">
                <div className="image-gallery-empty-icon">🖼️</div>
                <p>Inga bilder tillgängliga för denna session</p>
                <button className="btn btn-sm btn-outline-primary" onClick={refresh}>
                  Uppdatera
                </button>
              </div>
            ) : (
              <div className="image-list">
                <div className="image-gallery-count">
                  {imageCount} {imageCount === 1 ? 'bild' : 'bilder'}
                  {autoRefresh && <span className="auto-refresh-badge">⟳ Auto</span>}
                </div>
                {images.map((image) => (
                  <div
                    key={image.filename}
                    className="image-item"
                    onClick={() => handleImageClick(image)}
                  >
                    <ImageThumbnail
                      image={image}
                      sessionId={sessionId}
                      getImageBlobUrl={getImageBlobUrl}
                    />
                    <div className="image-item-info">
                      <p className="image-item-filename">{image.filename}</p>
                      <p className="image-item-timestamp">
                        {formatTimestamp(image.timestamp)}
                      </p>
                      <p className="image-item-size">{formatBytes(image.size)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal for enlargement */}
      {selectedImage && (
        <ImageModal
          image={selectedImage}
          onClose={handleCloseModal}
        />
      )}
    </>
  );
};

export default ImageGallery;
